import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Box, Typography, Chip } from '@mui/material';
import { friendlyNameConverter } from '../../../../services/localizationService';
import { extractValues, histogram } from '../statistics';
import { useTimeline } from '../TimelineContext';

export default function HistogramWidget({ recordings = [], widgetConfig }) {
  const { window: timeWindow } = useTimeline();
  const recId = widgetConfig?.recId || '';
  const sensorKey = widgetConfig?.sensorKey || '';
  const binCount = widgetConfig?.binCount || 20;

  const rec = recordings.find((r) => r.id === recId);

  const histData = useMemo(() => {
    if (!rec || !sensorKey) return null;
    const values = extractValues(rec, sensorKey, timeWindow?.start, timeWindow?.end);
    if (values.length === 0) return null;
    return histogram(values, binCount);
  }, [rec, sensorKey, timeWindow, binCount]);

  const sensorName = friendlyNameConverter(sensorKey || '');

  const chartOptions = histData ? {
    chart: { type: 'bar', toolbar: { show: false }, animations: { enabled: false } },
    plotOptions: { bar: { columnWidth: '95%' } },
    xaxis: {
      categories: histData.bins.map((b) => b.binStart.toFixed(1)),
      title: { text: sensorName },
      labels: { rotate: -45, style: { fontSize: '9px' } },
    },
    yaxis: { title: { text: 'Frequency' } },
    tooltip: {
      custom: ({ dataPointIndex }) => {
        const bin = histData.bins[dataPointIndex];
        return `<div style="padding:6px">${bin.binStart.toFixed(2)} – ${bin.binEnd.toFixed(2)}<br/>Count: <b>${bin.count}</b></div>`;
      },
    },
    dataLabels: { enabled: false },
    grid: { padding: { left: 10, right: 10 } },
  } : {};

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {histData ? (
        <Box sx={{ flex: 1, overflow: 'hidden', px: 1 }}>
          {timeWindow && <Chip label="Windowed" size="small" color="info" variant="outlined" sx={{ mb: 0.5, height: 18, fontSize: '0.6rem' }} />}
          <ReactApexChart type="bar" series={[{ name: 'Frequency', data: histData.bins.map((b) => b.count) }]} options={chartOptions} height="90%" />
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">Select a recording and sensor</Typography>
        </Box>
      )}
    </Box>
  );
}
